import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { forkJoin, Observable, of, Subscription } from 'rxjs';
import { WizardService } from 'src/app/wizard-bar/services/wizard.service';
import { QuotingModuleActionTypes, SetQuote, SetQuoteDueDate } from '@quoting/state/actions/quoting-module.actions';
import { QuoteRequestService } from '@app/shared/services/quote-request.service';
import { QuoteRequestState } from '@app/shared';
import { QuoteService } from '@app/quoting/services/quote.service';
import { map, tap } from 'rxjs/operators';
import { UserRole, UtilityHubIdentity } from '@auth/models/utility-hub-identity';
import { QuoteSettingsService } from '@app/quoting/services/quote-settings.service';
import { PricingSource, PricingSourceService } from '@app/shared/services/pricing-source.service';
import { QuoteRequestType } from '@app/shared/models/QuoteRequestType';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Offer } from '@app/quoting/models/quote';
import LogRocket from 'logrocket';
import { LoadingModalData } from '@app/_testing/fakes/fake-loading-modal.component';
import { LoadingModalComponent } from '@app/shared/components/loading-modal/loading-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { convertContractToMarginTerm, convertPriceToMarginTerm, removeDuplicatePrimitives } from '@app/shared/helpers/array-helper';
import { ContractLength } from '@app/shared/models/contract-length';
import { CommodityType } from '@app/shared/enums/commodity-type';
import { DecimalPipe } from '@angular/common';
import { MatrixService } from '@app/matrix/services/matrix.service';
import { loggedInUtilityHubIdentitySelector, quoteSelector, configurationSelector } from '@app/state/app.selectors';
import { AppState } from '@app/state/app.state';
import {
  SetContractLengthsSelected,
  SetMarginTerm,
  SetProductType,
  SetQuoteRequestType,
  SetPricesFilters,
  SetSweetSpotWasRequested,
  SetCommodityType,
  SetUtilityUOM,
  SetPageName,
  SetSweetSpotCheckBoxState,
  SetShowShareQuoteButton,
  SetGetQuoteContractLengthStartingButtons,
  SetMarginDollar,
} from '@utility-information/state/actions/utility-information.actions';
import {
  filteredPricesSelector,
  utilityUOMSelector,
  marginTermSelector,
} from '@utility-information/state/selectors/utility-information.selectors';
import { QuotationDeactivationCheckerService } from '@app/shared/services/quotation-deactivation-checker/quotation-deactivation-checker.service';
import { ConfirmDueDateComponent } from '@app/shared/components/confirm-due-date/confirm-due-date.component';
import { QuoteHelperService } from '@quoting/services/quote-helper.service';
import { QuoteRequestStatus } from '@app/shared/models/quote-request-status';
import { MarginTerm } from '@quoting/models/quote-request-summary';
import { SetOriginalQuoteRequestId } from '@app/renewals/state/actions/renewals-module.actions';
import { originalQuoteRequestIdSelector } from '@app/renewals/state/selectors/renewals-module.selectors';

@Component({
  selector: 'app-renewals-matrix-get-quote',
  templateUrl: './renewals-matrix-get-quote.component.html',
  styleUrls: ['./renewals-matrix-get-quote.component.scss'],
  providers: [QuotationDeactivationCheckerService],
})
export class RenewalsMatrixGetQuoteComponent implements OnInit, OnDestroy {
  @Input() pricingMethod: string;

  quoteRequestId: number;
  generatingLoadProfileData: boolean;
  quoteRequestState: QuoteRequestState;
  iDontKnowInQuoteRequest: boolean;
  storeQuoteRequestId: number;
  quote: any;
  user: UtilityHubIdentity;
  UserRole = UserRole;
  PricingSourceEnum = PricingSource;
  pricingSource: PricingSource;
  contractLengths$: Subscription;
  isPowwrSupplier: boolean;
  filteredPrices$: Observable<Offer[]>;
  utilityUOM$: Observable<string>;
  quoteStatus: string;
  marginTerm$: Observable<MarginTerm[]>;
  marginTerm: Array<MarginTerm> = [];
  selectQuote$: Subscription;
  isButtonQuoteDisabled: boolean = false;
  marginTermResults: MarginTerm[];
  isAllMarginChanged: boolean;
  pricingCurveDateObs: Observable<Date>;
  route$: Subscription;
  load$: Subscription;
  loadObs$: Observable<any>;
  loadProfile$: Subscription;
  pricingCurveDateDisplayed$: Subscription;
  uhIdentity$: Subscription;
  dialog$: Subscription;
  contractLengths: any;
  updateQuoteDueDate$: Subscription;
  frmGetQuote: FormGroup;
  upliftMax: number;
  upliftMin: number;
  showDetails = true;
  moreDetails = false;
  filteredPrices: Offer[];
  generatingQuote: boolean;
  originalQuoteRequestIdSelector$: Subscription;
  originalQuoteRequestId: number | null;

  private selectLoggedInUtilityHubIdentity$: Subscription;
  private hasIDontKnows: boolean;
  private inputAnnualUsage: number;

  public get brokerFeeCtrl() {
    return this.frmGetQuote?.get('upliftDollar');
  }

  public get isTaxIncluded() {
    return this.frmGetQuote?.get('isTaxIncluded');
  }

  public get supplierMarginCtrl() {
    return this.frmGetQuote?.get('marginDollar');
  }

  public get quoteDueDateCtrl() {
    let quoteDueDate = null;
    this.updateQuoteDueDate$ = this.store.select(quoteSelector).subscribe((x) => {
      let quote = x;
      quoteDueDate = quote.quoteDueDate;
    });
    return quoteDueDate;
  }

  constructor(
    public fb: FormBuilder,
    private dialog: MatDialog,
    public quoteRequestService: QuoteRequestService,
    private route: ActivatedRoute,
    private wizardService: WizardService,
    private store: Store<AppState>,
    private quoteService: QuoteService,
    public quoteMaxtrixService: MatrixService,
    private quoteSettingsService: QuoteSettingsService,
    private pricingSourceService: PricingSourceService,
    public quoteHelperService: QuoteHelperService,
    private changeDetection: ChangeDetectorRef,
    public quotationDeactivationCheckerService: QuotationDeactivationCheckerService
  ) {
    this.filteredPrices$ = this.store.select(filteredPricesSelector);
    this.utilityUOM$ = this.store.select(utilityUOMSelector);
    this.marginTerm$ = this.store.select(marginTermSelector);
  }

  ngOnDestroy(): void {
    this.uhIdentity$?.unsubscribe();
    this.route$?.unsubscribe();
    this.load$?.unsubscribe();
    this.loadProfile$?.unsubscribe();
    this.selectLoggedInUtilityHubIdentity$?.unsubscribe();
    this.selectQuote$.unsubscribe();
    this.pricingCurveDateDisplayed$.unsubscribe();
    this.updateQuoteDueDate$?.unsubscribe();
    this.originalQuoteRequestIdSelector$?.unsubscribe();
  }

  ngOnInit(): void {
    this.originalQuoteRequestIdSelector$ = this.store.select(originalQuoteRequestIdSelector).subscribe((originalQuoteRequestId) => {
      this.originalQuoteRequestId = originalQuoteRequestId;
    });

    this.wizardService.bindStep(this.route, () => this.save());
    this.route$ = this.route.params.subscribe((x) => {
      if (!isNaN(x.quoteRequestId)) {
        this.quoteRequestId = Number(x.quoteRequestId);
        if (this.quoteRequestId !== this.storeQuoteRequestId) {
          this.load();
        }
      }
      this.selectQuote$ = this.store.select(quoteSelector).subscribe((s) => {
        if (!s.quote) return;
        if (s.quote?.quoteRequestState?.status == QuoteRequestStatus.QuoteRequested && s.quote?.prices?.length > 0) {
          this.wizardService.stepClicked(this.wizardService.currentStep, false);
        }
        this.quoteService.pricingCurveDate.emit(s.quote);
      });
    });

    this.selectLoggedInUtilityHubIdentity$ = this.store
      .select(loggedInUtilityHubIdentitySelector)
      .pipe(
        tap((x) => (this.user = x)),
        tap(() => this.setMarginValidators())
      )
      .subscribe();

    this.pricingCurveDateDisplayed$ = this.quoteService.pricingCurveDate.subscribe((x) => {
      this.quote = x;
      this.changeDetection.detectChanges();
      this.setPricingCurveDate();
    });
  }

  load() {
    this.loadObs$ = forkJoin({
      state: this.quoteRequestService.getQuoteRequestState(this.quoteRequestId),
      quote: this.quoteRequestService.getSavedQuote(this.quoteRequestId),
      quoteConfiguration: this.quoteMaxtrixService.getQuoteConfiguration(this.quoteRequestId),
    }).pipe(
      map((result) => {
        const decimalPipe = new DecimalPipe('en-us');
        this.brokerFeeCtrl.patchValue(decimalPipe.transform(result.quote.upliftDollar, '1.2-4'));
        this.supplierMarginCtrl.patchValue(decimalPipe.transform(result.quote.marginDollar, '1.2-4'));

        if (result.quote.customerIsTaxExempt) {
          this.isTaxIncluded.patchValue(!result.quote.customerIsTaxExempt);
          this.isTaxIncluded.disable();
        } else this.isTaxIncluded.patchValue(result.quote?.isTaxIncluded);
        this.hasIDontKnows = result.quote.hasIDontKnows;
        const offersWithResults = result.quote.prices
          ?.filter((x) => !x.noResult)
          .sort((a, b) => this.quoteRequestService.sortSellingPrice(a, b));
        if (result.quote.prices?.length > 0) {
          this.store.dispatch(
            new SetContractLengthsSelected(
              this.quoteRequestId.toString(),
              result.quote.prices?.map((x) => x.term)
            )
          );
          this.marginTerm = convertPriceToMarginTerm(result.quote.prices as Offer[]);
          this.store.dispatch(new SetMarginTerm(this.quoteRequestId.toString(), this.marginTerm));
        } else {
          this.store.dispatch(
            new SetContractLengthsSelected(
              this.quoteRequestId.toString(),
              result.quoteConfiguration.contractLengths.map((x) => ({ months: x, selected: true } as ContractLength))
            )
          );
          this.marginTerm = convertContractToMarginTerm(
            result.quoteConfiguration.contractLengths.map((x) => ({ months: x, selected: true } as ContractLength)),
            result.quote.marginDollar
          );
          this.store.dispatch(new SetMarginTerm(this.quoteRequestId.toString(), this.marginTerm));
        }
        const offersWithNoResult = result.quote.prices?.filter((x) => x.noResult);
        const sortedOffers = offersWithResults.concat(offersWithNoResult);
        this.filteredPrices = sortedOffers;
        this.contractLengths = sortedOffers.map((p) => p.term as number);
        this.quoteRequestState = result?.quote?.quoteRequestState;
        this.store.dispatch(new SetOriginalQuoteRequestId(this.quoteRequestState.renewedFromQuoteId));

        let quote = result.quote as any;
        this.contractLengths = offersWithResults.map((p) => p.term as number);
        quote = {
          ...quote,
          prices: offersWithResults,
          marginDollar: this.isGasQuote() ? quote.marginDollar / 100 : quote.marginDollar,
          upliftDollar: this.isGasQuote() ? quote.upliftDollar / 100 : quote.upliftDollar,
        };
        this.quoteService.quoteUpdated.emit(quote);

        this.store
          .select(loggedInUtilityHubIdentitySelector)
          .subscribe((user) => {
            this.user = user;
            this.store.dispatch(new SetQuote(quote, user, result.state.hasIDontKnows));
            this.store.dispatch(new SetProductType(this.quoteRequestId.toString(), result.quote.productType));
            this.store.dispatch(new SetQuoteRequestType(this.quoteRequestId.toString(), QuoteRequestType.Matrix));
            this.store.dispatch(
              new SetPricesFilters(
                this.quoteRequestId.toString(),
                removeDuplicatePrimitives([...quote?.prices?.map((price) => price?.planType), 'Show All']),
                quote?.prices?.map((price) => price.term),
                true
              )
            );
            this.store.dispatch(new SetSweetSpotWasRequested(this.quoteRequestId.toString(), result?.state?.sweetSpotCheckbox));
            this.store.dispatch(new SetCommodityType(this.quoteRequestId.toString(), result.state.commodity));
            this.store.dispatch(new SetUtilityUOM(this.quoteRequestId.toString(), result.quote.uom));
            this.store.dispatch(new SetPageName(this.quoteRequestId.toString(), 'getquote'));
            this.store.dispatch(new SetSweetSpotCheckBoxState(this.quoteRequestId.toString(), true));
            this.store.dispatch(new SetShowShareQuoteButton(true));
            this.store.dispatch(
              new SetGetQuoteContractLengthStartingButtons(
                this.quoteRequestId.toString(),
                result.quote.prices?.map((x) => ({ months: x.term, selected: true } as ContractLength))
              )
            );
          })
          .unsubscribe();
        this.quoteStatus = result.quote?.quoteRequestState?.status;
        this.pricingSourceService.getPricingSource(QuoteRequestType.Matrix, result.state.commodity).subscribe((ps) => {
          this.pricingSource = ps.source;
        });
        this.store.select(configurationSelector).subscribe((config) => {
          this.isPowwrSupplier = config.supplierConfiguration.pricing.powwrSupplier && result.state.commodity !== CommodityType.Gas;
        });
      })
    );

    this.load$ = this.loadObs$.subscribe();
  }

  save(): Observable<any> {
    return of(true);
  }

  next() {
    this.wizardService.next();
  }

  back() {
    this.wizardService.previous();
  }

  onAcceptOrRejectOffer(actionTaken: QuotingModuleActionTypes) {
    // TODO this logic needs to move out into the get steps call on the backend where we can disable and enable active steps
    // We only want the user to proceed to the contract details screen next in the journey if they have accepted an offer, otherwise skip to the you're done page.
    if (actionTaken == QuotingModuleActionTypes.OfferAccept) {
      this.wizardService.next();
    } else {
      this.wizardService.advanceToLastStep();
    }
  }

  setMarginValidators() {
    const marginValidators = [Validators.pattern(/^\d{0,8}\.?\d{0,10}$/)];
    const upliftValidators = [Validators.min(this.upliftMin), Validators.max(this.upliftMax)];

    this.frmGetQuote = this.fb.group({
      upliftDollar: ['0', Validators.compose([Validators.required, Validators.pattern(/^\d{0,8}\.?\d{0,10}$/)])],
      marginDollar: ['0', Validators.compose(marginValidators)],
      isTaxIncluded: [true],
    });

    if (!this.user.hasRole(UserRole.SupplierManager)) {
      this.frmGetQuote.get('marginDollar').setValue(null);
    } else {
      marginValidators.push(Validators.required);
      this.frmGetQuote.get('marginDollar').setValidators(marginValidators);
      this.frmGetQuote.get('upliftDollar').setValidators(upliftValidators);
      this.frmGetQuote.updateValueAndValidity();
    }
  }

  marginDollarChanges(margin: number) {
    this.marginTermResults = this.marginTerm.map((obj) => {
      return { ...obj, margin: margin };
    });
    this.store.dispatch(new SetMarginTerm(this.quote?.quoteRequestState?.quoteRequestId.toString(), this.marginTermResults));
    this.store.dispatch(new SetMarginDollar(this.quote?.quoteRequestState?.quoteRequestId.toString(), margin));
    this.contractLengths$?.unsubscribe();
    this.isAllMarginChanged = true;
  }

  getQuoteDueDateUpdate(): void {
    const dialogRef = this.dialog.open(ConfirmDueDateComponent, {
      width: '30%',
      data: {
        title: `Quote Due Date`,
        message: 'Would you like to update the due date for this quote?',
        primaryButtonText: "Use today's date",
        secondaryButtonText: 'Update due date',
        quoteDueDateToolTip: 'Quote Due Date',
        todaysDate: new Date(),
      },
    });

    dialogRef.afterClosed().subscribe((isUseDefault) => {
      if (isUseDefault) this.store.dispatch(new SetQuoteDueDate(new Date(new Date().setHours(23, 59, 59, 0))));

      if (isUseDefault !== '' && typeof isUseDefault != 'undefined') this.getQuote();
    });
  }

  getQuote(): void {
    this.generatingQuote = true;

    LogRocket.track('request_quote', { quoteRequestType: this.quoteRequestState?.quoteRequestType });

    this.marginTerm$
      .subscribe((x) => {
        this.marginTerm = x;
      })
      .unsubscribe();

    this.dialog$ = this.dialog
      .open(LoadingModalComponent, {
        width: '25%',
        disableClose: true,
        data: {
          message: `Requesting a Quote`,
          unitOfWork: this.quoteRequestService.generateQuote(this.quoteRequestId, {
            quoteDueDate: this.quoteDueDateCtrl,
            marginDollar: this.supplierMarginCtrl?.value ?? 0,
            upliftDollar: this.brokerFeeCtrl.value ?? 0,
            standardPriceOnly: this.quoteRequestState?.quoteRequestType === QuoteRequestType.Matrix,
            isTaxIncluded: this.isTaxIncluded.value,
            marginTerm: this.marginTerm,
          }),
        } as LoadingModalData,
      })
      .afterClosed()
      .subscribe(
        (results) => {
          this.generatingQuote = false;
          if (results.results && results.quoteRequestState) {
            const offersWithNoResult = results.results?.filter((x) => x.noResult);
            const offersWithResults = results.results
              ?.filter((x) => !x.noResult)
              .sort((a, b) => this.quoteRequestService.sortSellingPrice(a, b));
            const sortedOffers = offersWithResults.concat(offersWithNoResult); // sort by cheapest selling price scaled up if < 1 year
            this.contractLengths = sortedOffers.map((p) => p.term as number);
            if (!results.error) {
              LogRocket.track('quote_returned', { quoteRequestType: this.quoteRequestState?.quoteRequestType });
              this.quote = {
                prices: offersWithResults,
                quoteDate: results.generatedDate,
                quoteRequestState: results.quoteRequestState,
                hasIDontKnows: results.quoteRequestState.hasIDontKnows,
                marginDollar: this.supplierMarginCtrl?.value ?? 0,
                upliftDollar: this.brokerFeeCtrl.value ?? 0,
                inputAnnualUsage: this.inputAnnualUsage,
                pricingCurveDate: results.pricingCurveDate,
              };
              if (this.supplierMarginCtrl?.value != 0 && this.pricingSource == this.PricingSourceEnum.Dynamic) {
                this.quote.prices?.map((x) => (x.margin = this.supplierMarginCtrl?.value));
                this.supplierMarginCtrl?.setValue(0);
                this.supplierMarginCtrl?.disable();
              } else {
                this.supplierMarginCtrl?.enable();
              }
              this.store.dispatch(
                new SetContractLengthsSelected(
                  this.quoteRequestId.toString(),
                  this.quote.prices?.map((x) => x.term)
                )
              );
              this.marginTerm = convertPriceToMarginTerm(this.quote.prices as Offer[]);
              this.store.dispatch(new SetMarginTerm(this.quoteRequestId.toString(), this.marginTerm));
              this.store.dispatch(new SetShowShareQuoteButton(true));

              this.uhIdentity$ = this.store.select(loggedInUtilityHubIdentitySelector).subscribe((user) => {
                this.store.dispatch(new SetQuote(this.quote, user, results.quoteRequestState.hasIDontKnows));
                this.store.dispatch(
                  new SetPricesFilters(
                    this.quoteRequestId.toString(),
                    removeDuplicatePrimitives([...this.quote?.prices?.map((price) => price?.planType), 'Show All']),
                    this.quote?.prices?.map((price) => price.term),
                    true
                  )
                );

                this.quoteSettingsService.updateOfferFilters({
                  contractLengths: offersWithResults.map((p) => p.term as number),
                  includeSweetSpot: true,
                });
              });
              if (this.quoteRequestId !== this.storeQuoteRequestId) {
                this.load();
              }
            } else {
              this.dispatchQuoteError(results);
            }
          } else {
            this.dispatchQuoteError(results);
          }
        },
        (err) => {
          this.generatingQuote = false;
          this.dispatchQuoteError({
            error: {
              ...err,
            },
          });
        }
      );
    this.isAllMarginChanged = false;
  }

  isQuoteButtonDisabled() {
    return false;
  }

  isGasQuote(): boolean {
    let commodityTypeStore;
    this.store.pipe().subscribe((x) => {
      commodityTypeStore = x.utilityInformationState.commodityType;
    });
    return commodityTypeStore == CommodityType.Gas;
  }

  showMoreOrLess(value: boolean) {
    this.moreDetails = value;
  }

  showRequestAQuoteButton() {
    return false;
  }

  setPricingCurveDate() {
    if (this.quote && this.quote?.pricingCurveDate) {
      this.pricingCurveDateObs = of(this.quote?.pricingCurveDate);
    }
  }

  isSupplierManager() {
    return this.user?.currentOrganisation?.role === UserRole.SupplierManager;
  }

  private dispatchQuoteError(results: any) {
    const quote = {
      error: {
        error: results.error.message,
        detail: results.error.messageDetail,
      },
      quoteRequestState: this.quoteRequestState,
      prices: [],
    };
    this.store
      .select(loggedInUtilityHubIdentitySelector)
      .subscribe((user) => {
        LogRocket.track('quote_errored', { results });
        this.store.dispatch(new SetQuote(quote, user, null));
      })
      .unsubscribe();
  }
}
